import type {
  CampaignCreationCapabilities,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import { IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  type EditableTargetValue,
  respectsDecimalPrecision,
  sourceLabelKey,
  type TargetRow,
  targetLabel,
} from "./strategy-target-model";

interface AddedTargetsPaneProps {
  branchKind: SmartCreationTargetingStrategyKind;
  addedRows: TargetRow[];
  selectedTargetId: string | null;
  selectedCount: number;
  capabilities: CampaignCreationCapabilities;
  mutationPending: boolean;
  isLaneActive: (target: TargetRow) => boolean;
  onInspectTarget: (id: string) => void;
  onUpdateTarget: (id: string, patch: Partial<EditableTargetValue>) => void;
  onTargetIncludedChange: (target: TargetRow, included: boolean) => void;
  onRemoveAll: () => void;
}

export function AddedTargetsPane({
  branchKind,
  addedRows,
  selectedTargetId,
  selectedCount,
  capabilities,
  mutationPending,
  isLaneActive,
  onInspectTarget,
  onUpdateTarget,
  onTargetIncludedChange,
  onRemoveAll,
}: AddedTargetsPaneProps) {
  const { t } = useTranslation("smartCreation");
  return (
    <DataTableFrame
      aria-label={t("strategy.editor.addedTitle")}
      data-target-pane="added"
      className="flex min-h-[442px] min-w-0 flex-col"
      viewportClassName="max-h-[440px] flex-1 overflow-auto"
      toolbar={
        <div className="flex w-full items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">
            {t("strategy.editor.addedTitle")} · {selectedCount}
          </h3>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="text-destructive"
            disabled={branchKind === "automatic" || selectedCount === 0}
            onClick={onRemoveAll}
          >
            {t("strategy.editor.removeAll")}
          </Button>
        </div>
      }
      footer={<span>{t("strategy.editor.scope")}</span>}
    >
      <DataTable density="compact" stickyHeader>
        <TableHeader>
          <TableRow>
            <TableHead>{t("strategy.editor.targetColumn")}</TableHead>
            <TableHead>{t("strategy.editor.bidColumn")}</TableHead>
            <TableHead>{t("strategy.editor.sourceColumn")}</TableHead>
            <TableHead className="w-10">
              <span className="sr-only">{t("strategy.editor.removeAll")}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {addedRows.map((target) => {
            const label = targetLabel(target.intent);
            const bidInvalid =
              target.bid < capabilities.bid.min ||
              target.bid > capabilities.bid.max ||
              !respectsDecimalPrecision(target.bid, capabilities.bid.decimals);
            return (
              <TableRow key={target.id} aria-selected={selectedTargetId === target.id}>
                <TableCell className="max-w-[180px]">
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto max-w-full justify-start px-0 text-left text-[11px]"
                    aria-label={t("strategy.editor.inspectTarget", { target: label })}
                    onClick={() => onInspectTarget(target.id)}
                  >
                    <span className="truncate">{label}</span>
                  </Button>
                  <div className="text-[10px] text-muted-foreground">
                    {target.intent.type === "keyword"
                      ? t(`review.match.${target.intent.matchType}`)
                      : target.intent.type === "category_brand"
                        ? t("strategy.editor.categoryBrand")
                        : t(`strategy.editor.${target.intent.type}`)}
                  </div>
                  {!isLaneActive(target) ? (
                    <Badge variant="neutral">{t("strategy.editor.inactiveStatus")}</Badge>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Input
                    size="sm"
                    className="w-24"
                    type="number"
                    min={capabilities.bid.min}
                    max={capabilities.bid.max}
                    step={10 ** -capabilities.bid.decimals}
                    value={target.bid}
                    aria-label={t("strategy.editor.bidForTarget", { target: label })}
                    aria-invalid={bidInvalid || undefined}
                    disabled={branchKind === "automatic"}
                    onChange={(event) => {
                      const bid = Number(event.currentTarget.value);
                      if (Number.isFinite(bid)) onUpdateTarget(target.id, { bid });
                    }}
                  />
                  {bidInvalid ? (
                    <span className="text-[10px] text-destructive">
                      {capabilities.bid.min}–{capabilities.bid.max} {capabilities.currencyCode}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell>{t(sourceLabelKey(target))}</TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("strategy.editor.removeTarget", { target: label })}
                    disabled={mutationPending || (branchKind === "automatic" && selectedCount <= 1)}
                    onClick={() => onTargetIncludedChange(target, false)}
                  >
                    <IconX />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
          {addedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                {t("strategy.editor.emptyTitle")}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </DataTable>
    </DataTableFrame>
  );
}
